import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { MaskService } from '@shared/services/mask.service';
import { CstIssqnStruct } from '@shared/services/structs/motor-fiscal/cst-issqn.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { getIndicadorIssTexto, IndicadorIssTexto } from '@shared/constants/motor-fiscal/indicador-iss-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';

/** Campo do indicador de exigibilidade do ISS -> control da regra da natureza (aba e wizard de exceção usam os mesmos nomes). */
export const MAPA_CAMPOS_ISS_NATUREZA: MapaCamposIndicador = {
    nProcesso: 'numeroProcesso',
};

@Component({
    selector: 'app-issqn-tributo-regra-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
    ],
    templateUrl: './tributo-regra-section.component.html',
    styleUrl: './tributo-regra-section.component.css',
})
export class IssqnTributoRegraSectionComponent implements OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstIssqnStruct[] = [];
    @Input() variaveis: VariavelStruct[] = [];
    @Input() personalizadasCount = 0;
    @Input() modoExcecao = false;
    @Input() personalizarDisabled = false;
    /** Tipo de operação da natureza: Entrada ou Saída. Filtra os indicadores pela operação. */
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Output() personalizar = new EventEmitter<void>();

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getCurrencyMask();

    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() indicadoresIncentivo: LookupItemStruct[] = [];

    constructor(
        private maskService: MaskService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] && this.form) {
            // Zera os campos que o novo indicador não exibe (evita valor obsoleto no payload).
            this.form.get('indicadorIss')?.valueChanges.subscribe(() =>
                limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_ISS_NATUREZA));
        }
    }

    public get indicadores(): IndicadoresCst {
        const codigo = this.form?.get('indicadorIss')?.value ?? null;
        return lerIndicadoresCst(this.codigoTributos.find(x => x.codigo === codigo)?.indicadores);
    }

    public get texto(): IndicadorIssTexto | null {
        const codigo = this.form?.get('indicadorIss')?.value ?? null;
        return getIndicadorIssTexto(codigo, this.codigoTributos.find(x => x.codigo === codigo)?.descricao, this.indicadores);
    }

    /** Só os indicadores da operação (entrada ou saída) da natureza, pelo indicador do catálogo. */
    public get codigoTributosDaOperacao(): CstIssqnStruct[] {
        return this.codigoTributos.filter(c => isCodigoDaOperacao(c.indicadores, this.tipoOperacao));
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    /** True quando o botão "Personalizar fórmulas" não pode ser acessado. */
    public get personalizarBloqueado(): boolean {
        return this.personalizarDisabled || !this.form?.get('indicadorIss')?.value;
    }

    /** Mensagem do tooltip exibida apenas quando o botão está desabilitado. */
    public get personalizarTooltip(): string {
        if (this.personalizarDisabled) {
            return this.modoExcecao
                ? 'A personalização de fórmulas não está disponível para exceções.'
                : 'A personalização de fórmulas está indisponível no momento.';
        }
        if (!this.form?.get('indicadorIss')?.value) {
            return 'Selecione um Código para habilitar a personalização de fórmulas.';
        }
        return '';
    }

    public onPersonalizar(): void {
        this.personalizar.emit();
    }
}
